/**
 * The milk the farms give per day and where it goes (US-7 A, Ronald on the decision sheet, 21 October 2026): one bar
 * per day, split into Gwalchmai as the factory ordered it, the buyers' barrels and what is still to trade. The three
 * add up to the day's milk in. Trading and the factory's silos are two different things (Ronald, 21 October): the
 * barrels never pass through the silo graph on Milk Flow, and this graph is where they are.
 *
 * A firm barrel (a contract, or an offer the buyer agreed to) is solid; one proposed, offered or tendered is drawn
 * as expected, like every expected figure on these pages. The source is our producers until milk bought from
 * others is recorded (US-10 A): it then gets its own row. The table under the bars is the legend and what a screen
 * reader reads; the drawing has no words beyond the days and the litres.
 */
import { Table } from "@depicomnlbv/platform-ui";
import type { ReactNode } from "react";

import type { TradeBuyer, TradeDay } from "../api/milkFlow";
import { Figure } from "../pages/MilkFlow";
import { formatLitres } from "../time";

const HEIGHT = 220;
const TOP = 12;
const BOTTOM = 22;
const LEFT = 64;
const RIGHT = 8;
const DAY_WIDTH = 56;
const BAR_WIDTH = 30;
const STEP = 20000;

const WEEKDAY = new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", weekday: "short", day: "numeric" });
const dayLabel = (day: string) => WEEKDAY.format(new Date(`${day}T00:00:00Z`));
const litres = (n: number) => formatLitres(n);

/** A buyer's name as the page shows it: an open tender round has none yet. */
const buyerName = (b: TradeBuyer) => b.buyer ?? "Open tender";

type Part = { key: string; litres: number; className: string };

function parts(d: TradeDay): Part[] {
  const firm = d.buyers.filter((b) => b.firm).reduce((n, b) => n + b.to_barrels, 0);
  const expected = d.buyers.filter((b) => !b.firm).reduce((n, b) => n + b.to_barrels, 0);
  return [
    { key: "gwalchmai", litres: d.gwalchmai_litres, className: "mi-bar is-gwalchmai" },
    { key: "firm", litres: firm, className: "mi-bar is-traded" },
    { key: "expected", litres: expected, className: "mi-bar is-traded is-expected" },
    { key: "still", litres: d.still_to_trade_litres, className: "mi-bar is-still" },
  ];
}

function describe(d: TradeDay): string {
  const traded = d.buyers.map((b) => `${buyerName(b)} ${litres(b.to_barrels)}`).join(", ");
  return [
    `${dayLabel(d.day)}: ${litres(d.intake_litres)} in`,
    `Gwalchmai ${litres(d.gwalchmai_litres)}`,
    traded ? `traded ${traded}` : null,
    d.still_to_trade_litres > 0 ? `still to trade ${litres(d.still_to_trade_litres)}` : null,
  ]
    .filter(Boolean)
    .join("; ");
}

export function MilkIntakeChart({ days }: { days: TradeDay[] }) {
  const width = LEFT + RIGHT + days.length * DAY_WIDTH;
  const plotH = HEIGHT - TOP - BOTTOM;
  const highest = Math.max(STEP, ...days.map((d) => d.intake_litres));
  const top = Math.ceil(highest / STEP) * STEP;
  const y = (l: number) => TOP + plotH - (l / top) * plotH;
  const ticks = Array.from({ length: top / STEP + 1 }, (_, i) => i * STEP);
  const still = days.reduce((n, d) => n + d.still_to_trade_litres, 0);
  const first = days[0];
  const last = days[days.length - 1];
  const label = first && last
    ? `Milk in from ${dayLabel(first.day)} to ${dayLabel(last.day)}, ${litres(still)} still to trade`
    : "Milk in";
  return (
    <div className="mf-scroll">
      <svg aria-label={label} className="mi-svg" role="img" viewBox={`0 0 ${width} ${HEIGHT}`} width={width}>
        {ticks.map((t) => (
          <g key={t}>
            <line className="mf-tl__grid" x1={LEFT} x2={width - RIGHT} y1={y(t)} y2={y(t)} />
            <text className="mf-tl__tick" textAnchor="end" x={LEFT - 6} y={y(t) + 4}>
              {t === 0 ? "0" : `${t / 1000}k L`}
            </text>
          </g>
        ))}
        {days.map((d, i) => {
          const x = LEFT + i * DAY_WIDTH + (DAY_WIDTH - BAR_WIDTH) / 2;
          let base = 0;
          return (
            <g key={d.day}>
              <title>{describe(d)}</title>
              {parts(d)
                .filter((p) => p.litres > 0)
                .map((p) => {
                  const y0 = y(base);
                  base += p.litres;
                  const y1 = y(base);
                  return <rect className={p.className} height={y0 - y1} key={p.key} width={BAR_WIDTH} x={x} y={y1} />;
                })}
              <text className="mf-tl__tick" textAnchor="middle" x={x + BAR_WIDTH / 2} y={HEIGHT - 6}>
                {dayLabel(d.day)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/** The figures under the bars: each row's swatch is the bar's colour, so the table is the legend. */
export function MilkIntakeTable({ days }: { days: TradeDay[] }) {
  const buyers = [...new Set(days.flatMap((d) => d.buyers.map(buyerName)))];
  const cell = (key: string, content: ReactNode) => (
    <td className="dpc-table__num" key={key}>
      {content}
    </td>
  );
  const none = <span className="nothing">—</span>;
  return (
    <div className="mf-scroll">
      <Table caption="Milk in per day and where it goes" captionHidden className="milk-flow-table mi-table">
        <thead>
          <tr>
            <td />
            {days.map((d) => (
              <th className="dpc-table__num" key={d.day} scope="col">
                {dayLabel(d.day)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="is-total">
            <th scope="row">Milk in</th>
            {days.map((d) => cell(d.day, <Figure expected value={litres(d.intake_litres)} />))}
          </tr>
          <tr>
            <th scope="row">
              <span aria-hidden="true" className="mi-swatch is-gwalchmai" />
              Gwalchmai
            </th>
            {days.map((d) => cell(d.day, <Figure expected value={litres(d.gwalchmai_litres)} />))}
          </tr>
          {buyers.map((name) => {
            const all = days.flatMap((d) => d.buyers.filter((b) => buyerName(b) === name));
            const anyFirm = all.some((b) => b.firm);
            return (
              <tr key={name}>
                <th scope="row">
                  <span aria-hidden="true" className={`mi-swatch is-traded${anyFirm ? "" : " is-expected"}`} />
                  {name}
                </th>
                {days.map((d) => {
                  const mine = d.buyers.filter((b) => buyerName(b) === name);
                  if (mine.length === 0) return cell(d.day, none);
                  const total = mine.reduce((n, b) => n + b.to_barrels, 0);
                  // What the day's J.H. Willis farms cannot give of these barrels, in the critical colour.
                  const short = mine.reduce((n, b) => n + b.litres - b.to_barrels, 0);
                  const firm = mine.every((b) => b.firm);
                  return cell(
                    d.day,
                    <>
                      {total > 0 ? <Figure expected={!firm} value={litres(total)} /> : none}
                      {short > 0 ? (
                        <span className="mf-cell-sub">
                          <Figure short value={`−${litres(short)}`} />
                        </span>
                      ) : null}
                    </>,
                  );
                })}
              </tr>
            );
          })}
          <tr>
            <th scope="row">
              <span aria-hidden="true" className="mi-swatch is-still" />
              Still to trade
            </th>
            {days.map((d) =>
              cell(d.day, d.still_to_trade_litres > 0 ? <Figure expected value={litres(d.still_to_trade_litres)} /> : none),
            )}
          </tr>
        </tbody>
      </Table>
    </div>
  );
}

export function MilkIntake({ days }: { days: TradeDay[] }) {
  if (days.length === 0) return null;
  return (
    <>
      <MilkIntakeChart days={days} />
      <MilkIntakeTable days={days} />
    </>
  );
}
